/**
 * @fileoverview ToolCallMessage component for rendering tool calls in chat.
 * Uses a data-driven approach with configuration objects instead of
 * repetitive conditional blocks.
 */

'use client';

import type { DynamicToolUIPart, ToolUIPart } from 'ai';
import { AlertCircle, CheckCircle2, Loader2, Target, XCircle } from 'lucide-react';
import {
  AgentCallCardRenderer,
  AnalyticsPlanRenderer,
  AskQuestionRenderer,
  AskRenderer,
  CompleteRenderer,
  ExecuteAnalyticsPlanRenderer,
  FileToolRenderer,
  SpecRenderer,
} from './renderers';
import {
  TOOL_CONFIGS,
  getBrowserToolText,
  getComputerToolText,
  getDatasetToolText,
} from './tool-configs';
import { BrowserToolView } from '../../tool-views/wrapper/browser';
import type { AgentCallStreamStats } from '../agent-call-stream-state';
import { formatToolName } from './format-tool-name';
import { toolNameOfPart } from './tool-part';
import { useToolVisibility } from '@/hooks/use-tool-visibility';




/**
 * Props for the ToolCallMessage component.
 */
interface ToolCallMessageProps {
  /**
   * The tool call to draw. Either shape the AI SDK produces: a statically
   * declared `tool-<name>` part, or the `dynamic-tool` part every
   * runtime-discovered tool (Teammate Code's sandbox engine, MCP, Composio)
   * arrives as, which carries its name in a field instead of in the type.
   */
  part: ToolUIPart | DynamicToolUIPart;
  /** Optional sandbox identifier for file operations */
  sandboxId?: string;
  /** Whether this tool call is in the last message (for interactive renderers) */
  isLastMessage?: boolean;
  /** Text from the next user message, used to restore submitted form values */
  nextUserMessageText?: string;
  /** Whether the parent message is actively streaming. When false, input-available state means incomplete. */
  isMessageStreaming?: boolean;
  /** Accumulated streaming text per agent ID for live agent call previews */
  agentStreams?: Record<string, string>;
  /** Live display metrics per streamed agent ID */
  agentStreamStats?: Record<string, AgentCallStreamStats>;
  /** Finalized streamed text per agent ID, preserved after stream-end */
  agentFinalTexts?: Record<string, string>;
}

/**
 * Determines whether a tool call completed successfully based on its output.
 *
 * Decision order:
 *   1. Null output → failure (the tool never returned).
 *   2. `success: false` → failure (explicit boolean from the tool).
 *   3. `status === 'error'` → failure (shape that `tool-runtime.ts`
 *      emits when it catches a thrown error: `{ status: 'error', message }`).
 *   4. `error` field truthy → failure.
 *   5. Bare string starting with `Error:` / `Failed:` (case-insensitive,
 *      optional leading whitespace) → failure. Anything else is success.
 *
 * We deliberately do NOT substring-scan free-form text for the words
 * "error"/"fail" — legitimate tool output (e.g. an MCP wiki structure that
 * lists an "Error Boundaries" section) would otherwise be marked as failure.
 */
function isToolCallSuccessful(output: Record<string, unknown> | null): boolean {
  if (!output) return false;
  if (typeof output.success === 'boolean') return output.success;
  if (typeof output === 'object' && output.status === 'error') return false;
  if (output.error) return false;
  if (typeof output === 'string' && /^\s*(error|failed)[:\s]/i.test(output)) return false;
  return true;
}

/**
 * Common input field names that contain useful context for display.
 * Ordered by relevance — first match wins.
 */
const DETAIL_FIELDS = [
  'query', 'name', 'title', 'subject', 'url', 'to', 'filename',
  'file_path', 'filePath', 'path', 'command', 'action', 'range', 'ranges',
  'topic', 'keyword', 'prompt', 'spreadsheet_name', 'instruction', 'email',
  // What a search tool names its target. Without it a run of greps reads as the
  // same unlabelled row repeated, with nothing to tell one search from the next.
  'pattern',
];

/**
 * Extracts a short meaningful detail from tool input args.
 * Looks for common field names and returns the first short value found.
 * Handles both string and array values.
 */
function extractToolDetail(input: Record<string, unknown>): string | undefined {
  for (const field of DETAIL_FIELDS) {
    const value = input[field];
    if (typeof value === 'string' && value.length > 0 && value.length < 120) {
      return value;
    }
    // Handle arrays (e.g. ranges: ["products!A1:H25"])
    if (Array.isArray(value) && value.length > 0) {
      const first = value[0];
      if (typeof first === 'string' && first.length > 0 && first.length < 120) {
        return value.length > 1 ? `${first} (+${value.length - 1} more)` : first;
      }
    }
  }
  return undefined;
}

function extractSubAgentHistoryTarget(
  input: Record<string, unknown>,
  output: Record<string, unknown> | null,
): string | undefined {
  const outputName = output?.targetName;
  if (typeof outputName === 'string' && outputName.trim().length > 0) {
    return outputName.trim();
  }

  const fields = ['agentName', 'workerName', 'name', 'agentId', 'workerId', 'workerKey', 'threadId'];

  for (const field of fields) {
    const value = input[field];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }

  return undefined;
}

/**
 * Detects the screenshot shapes supported by the browser tool view.
 * Only screenshot outputs need a rich inline preview; other browser actions
 * should stay as compact status rows in the chat timeline.
 */
function hasBrowserScreenshot(output: Record<string, unknown> | null): boolean {
  if (!output) return false;
  const screenshot = output.screenshot;
  return (
    (typeof screenshot === 'object' &&
      screenshot !== null &&
      typeof (screenshot as { imageUrl?: unknown }).imageUrl === 'string') ||
    typeof output.image_url === 'string' ||
    typeof output.screenshotUrl === 'string' ||
    typeof output.imageUrl === 'string' ||
    typeof output.screenshot_base64 === 'string'
  );
}

const ODOO_MODEL_LABELS: Record<string, string> = {
  'account.move': 'Accounting',
  'crm.lead': 'CRM',
  'crm.team': 'Sales teams',
  'hr.department': 'HR departments',
  'hr.employee': 'HR',
  'ir.module.module': 'Apps',
  'product.product': 'Products',
  'product.template': 'Products',
  'res.partner': 'Contacts',
  'sale.order': 'Sales',
};

function parseRequestBody(body: unknown): Record<string, unknown> | undefined {
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    return body as Record<string, unknown>;
  }

  if (typeof body !== 'string') return undefined;

  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function getOdooAreaLabel(model: string): string {
  return ODOO_MODEL_LABELS[model] ?? model.split('.')[0]?.toUpperCase() ?? 'Odoo';
}

function getApiRequestText(
  input: Record<string, unknown>,
  isLoading: boolean,
  failed: boolean,
): string {
  const body = parseRequestBody(input.body);
  const path = typeof input.path === 'string' ? input.path : '';
  const model = typeof body?.model === 'string' ? body.model : undefined;
  const isOdoo = path.includes('/jsonrpc') || !!model;

  if (isOdoo) {
    const target = model ? `Odoo ${getOdooAreaLabel(model)}` : 'Odoo';
    if (isLoading) return `Checking ${target}`;
    return failed ? `Couldn't check ${target}` : `Checked ${target}`;
  }

  if (isLoading) return 'Calling external API';
  return failed ? 'External API call failed' : 'Called external API';
}

/**
 * Renders a tool call message with appropriate styling and content.
 * Uses configuration-driven rendering for most tools, with special
 * renderers for complex tools like ask, complete, and analytics.
 */
export function ToolCallMessage({ part, sandboxId, isLastMessage = false, nextUserMessageText, isMessageStreaming = false, agentStreams, agentStreamStats, agentFinalTexts }: ToolCallMessageProps) {
  const { toolCallId, state } = part;
  const payloadPart = part as ToolUIPart & {
    input?: Record<string, unknown>;
    output?: Record<string, unknown>;
  };

  /**
   * Workspace-level tool-output visibility (issue #1332). When false, the tool
   * call itself still renders but its output/result — including error/failure
   * output — is suppressed. Applied below, after the content renderers that
   * legitimately consume their own output (e.g. ask_user_question).
   */
  const { showToolOutput, resolved: toolVisibilityResolved } = useToolVisibility();
  const outputHidden = !toolVisibilityResolved || !showToolOutput;

  // Extract tool invocation details
  const input: Record<string, unknown> = payloadPart.input || {};
  const toolName = toolNameOfPart(part) || '';
  let output: Record<string, unknown> | null =
    state === 'output-available' ? payloadPart.output || null : null;

  /**
   * A tool call is "loading" only if it hasn't completed AND the message is still streaming.
   * Historical tool calls with `input-available` state (never completed) should not spin forever.
   */
  const isIncomplete = state === 'input-available' && !isMessageStreaming;
  const isLoading = state !== 'output-available' && state !== 'output-error' && !isIncomplete;

  // Don't render for these tool names
  if (toolName === 'done' || toolName === 'finish_and_respond') {
    return null;
  }

  // Special renderers for content tools
  if (toolName === 'ask') {
    return <AskRenderer input={input} sandboxId={sandboxId} />;
  }

  // ask_user_question — styled answered/pending bubble per issue #975 wireframe.
  if (toolName === 'ask_user_question') {
    return (
      <AskQuestionRenderer
        input={input as Parameters<typeof AskQuestionRenderer>[0]['input']}
        output={output as Parameters<typeof AskQuestionRenderer>[0]['output']}
      />
    );
  }

  if (toolName === 'complete') {
    return <CompleteRenderer input={input} sandboxId={sandboxId} />;
  }

  // json-render unified spec renderer — only render once the full spec is received
  // During streaming, the elements map may be incomplete and partially-parsed
  // elements can have null props, crashing the json-render resolver.
  if (toolName === 'render_ui') {
    // No tool-call chrome in chat: while the spec is still streaming render
    // nothing (the agent's own line already tells the user what's coming), then
    // show only the rendered UI itself — never a "tool is running" indicator.
    if (isLoading) {
      return null;
    }
    return <div className="my-3"><SpecRenderer input={input} isLastMessage={isLastMessage} submittedText={nextUserMessageText} sandboxId={sandboxId} /></div>;
  }

  /**
   * Suppress tool output once the content renderers above have run, so the
   * activity renderers below show the call without its result. Nulling output
   * cascades through every downstream branch (screenshots, output detail,
   * agent-call cards) without per-branch checks (issue #1332).
   */
  if (outputHidden) {
    output = null;
  }

  /**
   * Renders a simple inline status icon based on tool state. When output is
   * hidden we never surface a failure state (the whole point is to keep
   * error/failure output off-screen), so the icon stays neutral.
   */
  const renderIcon = (overrideSuccess?: boolean) => {
    if (isLoading) {
      return <Loader2 className="size-3.5 animate-spin shrink-0" />;
    }
    const success = overrideSuccess ?? (outputHidden ? true : isToolCallSuccessful(output));
    if (!success) {
      return <XCircle className="size-3.5 text-destructive shrink-0" />;
    }
    return <CheckCircle2 className="size-3.5 shrink-0" />;
  };

  // Container wrapper — inline muted text, no chip/badge
  const renderContainer = (children: React.ReactNode) => (
    <div
      id={toolCallId}
      className="flex items-center gap-1.5 my-2 text-sm text-muted-foreground"
    >
      {children}
    </div>
  );

  if (toolName === 'web_browse' && hasBrowserScreenshot(output)) {
    return (
      <div className="my-3 max-w-3xl overflow-hidden rounded-lg border border-border bg-background shadow-sm" id={toolCallId}>
        <div className="flex items-center gap-1.5 border-b border-border/70 px-3 py-2 text-sm text-muted-foreground">
          {renderIcon()}
          <span className="min-w-0 truncate">
            {TOOL_CONFIGS.web_browse.getOutputDetail?.(output, input) ?? 'Captured browser screenshot'}
          </span>
        </div>
        <div className="h-[360px] sm:h-[420px]">
          {/* `web_browse` is a statically declared platform tool, so the part is
              always the `tool-<name>` shape here — never the dynamic one. */}
          <BrowserToolView tool={part as ToolUIPart} />
        </div>
      </div>
    );
  }

  // Special renderer: file_tool
  if (toolName === 'file_tool') {
    return renderContainer(
      <FileToolRenderer isLoading={isLoading} input={input} />
    );
  }

  // Special renderer: create_data_analytics_plan
  if (toolName === 'create_data_analytics_plan') {
    return renderContainer(
      <AnalyticsPlanRenderer
        isLoading={isLoading}
        output={output || undefined}
        IconComponent={Target}
      />
    );
  }

  // Special renderer: create_execute_analytics_plan
  if (toolName === 'create_execute_analytics_plan') {
    return renderContainer(
      <ExecuteAnalyticsPlanRenderer
        isLoading={isLoading}
        output={output || undefined}
      />
    );
  }

  // Special renderer: make_agent_call (minimal collapsible style)
  if (toolName === 'make_agent_call') {
    const streamStats = typeof input.agentId === 'string' ? agentStreamStats?.[input.agentId] : undefined;
    const streamingText = typeof input.agentId === 'string' ? agentStreams?.[input.agentId] : undefined;
    const finalText = typeof input.agentId === 'string' ? agentFinalTexts?.[input.agentId] : undefined;
    const cardIsLoading = isLoading && !streamStats?.endedAt;
    const agentCallStatus = cardIsLoading
      ? 'Running'
      : isIncomplete
        ? 'Incomplete'
        : isToolCallSuccessful(output) || !!finalText
          ? 'Completed'
          : 'Failed';
    return (
      <div className="my-3" id={toolCallId}>
        <AgentCallCardRenderer
          input={input}
          output={output}
          isLoading={cardIsLoading}
          isIncomplete={isIncomplete}
          status={agentCallStatus}
          streamingText={streamingText}
          streamStats={streamStats}
          finalText={finalText}
        />
      </div>
    );
  }

  // Special renderer: spawn_worker_agents renders one collapsible line per
  // worker, matching the make_agent_call card style (image.png) instead of the
  // older non-collapsible box.
  if (toolName === 'spawn_worker_agents') {
    const workers = Array.isArray(input.workers) ? input.workers : [];
    const outputWorkers = Array.isArray(output?.workers) ? output.workers : [];
    const findOutputForWorker = (workerKey: string) =>
      outputWorkers.find((worker: Record<string, unknown>) => worker.workerKey === workerKey);

    return (
      <div className="my-3 space-y-1.5" id={toolCallId}>
        {workers.map((worker: Record<string, unknown>) => {
          const workerKey = typeof worker.workerKey === 'string' ? worker.workerKey : worker.name;
          const normalizedWorkerKey = typeof workerKey === 'string' ? workerKey : undefined;
          const workerOutput = typeof workerKey === 'string' ? findOutputForWorker(workerKey) : undefined;
          const workerFailed = workerOutput?.status === 'failed';
          const streamStats = normalizedWorkerKey ? agentStreamStats?.[normalizedWorkerKey] : undefined;
          const workerIsLoading = isLoading && !streamStats?.endedAt;
          const workerStatus = workerIsLoading
            ? 'Running'
            : isIncomplete
              ? 'Incomplete'
              : workerFailed
                ? 'Failed'
                : 'Completed';

          // Map the worker's fields onto the shared agent-call card. The card
          // picks streaming → final → output in that order for its preview, so
          // we surface the worker's saved output string as finalText.
          const streamingText = normalizedWorkerKey ? agentStreams?.[normalizedWorkerKey] : undefined;
          const finalText =
            (normalizedWorkerKey ? agentFinalTexts?.[normalizedWorkerKey] : undefined) ??
            (typeof workerOutput?.output === 'string' ? workerOutput.output : undefined);
          const cardOutput =
            workerOutput ?? (typeof output?.message === 'string' ? output.message : null);

          return (
            <AgentCallCardRenderer
              key={normalizedWorkerKey || String(worker.name)}
              input={{
                agentName: typeof worker.name === 'string' ? worker.name : 'Temporary worker',
                status: typeof worker.status === 'string' ? worker.status : undefined,
                explanation: typeof worker.task === 'string' ? worker.task : undefined,
                roleDescription: typeof worker.role === 'string' ? worker.role : undefined,
                instructions: Array.isArray(worker.acceptanceCriteria)
                  ? worker.acceptanceCriteria.filter((item): item is string => typeof item === 'string').join('\n')
                  : undefined,
              }}
              output={cardOutput}
              isLoading={workerIsLoading}
              isIncomplete={isIncomplete || workerFailed}
              status={workerStatus}
              streamingText={streamingText}
              streamStats={streamStats}
              finalText={finalText}
            />
          );
        })}
      </div>
    );
  }

  // Browser tools
  if (toolName.startsWith('browser')) {
    return renderContainer(
      <>
        {renderIcon()}
        <span>{getBrowserToolText(toolName, input)}</span>
      </>
    );
  }

  // Dataset tools
  if (toolName.startsWith('dataset_')) {
    return renderContainer(
      <>
        {renderIcon()}
        <span>{getDatasetToolText(toolName, input)}</span>
      </>
    );
  }

  // Computer control tools
  if (toolName.startsWith('computer_')) {
    return renderContainer(
      <>
        {renderIcon()}
        <span>{getComputerToolText(toolName, input)}</span>
      </>
    );
  }

  if (toolName === 'read_sub_agent_history') {
    const target = extractSubAgentHistoryTarget(input, output);

    return renderContainer(
      <>
        {renderIcon()}
        <span className="truncate min-w-0">
          Reading{' '}
          {target ? (
            <strong className="font-semibold text-foreground">{target}</strong>
          ) : (
            <strong className="font-semibold text-foreground">sub-agent</strong>
          )}
          {"'s conversation with the agent"}
        </span>
      </>
    );
  }

  if (toolName === 'api_request') {
    const apiRequestFailed =
      !outputHidden &&
      (state === 'output-error' ||
        isIncomplete ||
        (output ? !isToolCallSuccessful(output) : false));

    return renderContainer(
      <>
        {renderIcon()}
        <span className="truncate min-w-0">
          {getApiRequestText(input, isLoading, apiRequestFailed)}
        </span>
      </>
    );
  }

  // Sandbox execute command
  if (toolName === 'sandbox_execute_command') {
    const cmdSuccess = output?.data?.exitCode === 0 || output?.data?.completed === true;
    return renderContainer(
      <>
        {renderIcon(output ? cmdSuccess : undefined)}
        <span>
          {isLoading ? 'Executing' : 'Executed'}{' '}
          <span className="font-mono">{input.command || 'shell command'}</span>
        </span>
      </>
    );
  }

  /**
   * Interrupted tool calls (historical, never finished). The call still says
   * what it was *trying* to do — so show the same "<Tool>: <detail>" label the
   * completed call would have shown, plus an explicit `interrupted` marker.
   * Falling back to the literal word "incomplete" as the detail (issue #1458)
   * produced meaningless rows like "Web search: incomplete" for any tool with
   * no `TOOL_CONFIGS` entry.
   */
  if (isIncomplete) {
    const formattedName = formatToolName(toolName.replace(/^sandbox_/, ''));
    const detail = TOOL_CONFIGS[toolName]?.getDetail?.(input) || extractToolDetail(input);

    return renderContainer(
      <>
        <AlertCircle className="size-3.5 text-amber-500 shrink-0" />
        <span className="truncate min-w-0">
          {formattedName}
          {detail ? `: ${detail}` : ''}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">interrupted</span>
      </>
    );
  }

  // LLM-generated status text (best quality) — use completedText when done,
  // statusText while in-progress. The model authors `completedText`
  // optimistically ("Python run complete") without knowing the real exit
  // status, so on a FAILED tool call it clashes with the ✗ error icon. When
  // the output indicates failure, prefer the neutral in-progress `statusText`
  // (the action being attempted) so the label reads correctly alongside the
  // error icon instead of falsely claiming success.
  const toolFailed =
    !isLoading && !outputHidden && output != null && !isToolCallSuccessful(output);
  const lmStatusText = isLoading
    ? input.statusText
    : toolFailed
      ? input.statusText || input.completedText
      : input.completedText || input.statusText;

  if (lmStatusText) {
    return renderContainer(
      <>
        {renderIcon()}
        <span className="truncate min-w-0">{lmStatusText}</span>
      </>
    );
  }

  // Fallback: static config for known tools
  const config = TOOL_CONFIGS[toolName];

  if (config) {
    const inputDetail = config.getDetail?.(input);
    const outputDetail = output && config.getOutputDetail ? config.getOutputDetail(output, input) : undefined;

    // When outputDetail exists it is self-contained ("Searched web: query"),
    // so skip config.text to avoid duplication.
    const detail = outputDetail || inputDetail;
    const showLabel = !outputDetail && config.text;

    return renderContainer(
      <>
        {renderIcon()}
        {showLabel && <span className="whitespace-nowrap">{config.text}</span>}
        {detail && <span className="truncate min-w-0">{detail}</span>}
      </>
    );
  }

  // Unknown tools (Composio, MCP, etc.) — humanize name + extract detail from args
  const formattedToolName = formatToolName(toolName);
  const detail = extractToolDetail(input);

  return renderContainer(
    <>
      {renderIcon()}
      <span className="truncate min-w-0">
        {formattedToolName}{detail ? `: ${detail}` : isLoading ? '...' : ''}
      </span>
    </>
  );
}

export default ToolCallMessage;
